import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { CheckCircle2, CircleDashed, XCircle } from 'lucide-react';
import type { AppDispatch, RootState } from '../../configuration';
import {
  EligibilityItem,
  EligibilityKind,
  EligibilityResult,
  getEligibility,
} from '../../api/user/eligibility.api';
import { authenticationServices } from '../../services/authentication.service';
import { fetchProfile } from '../../redux/features/auth/slice';
import { routePaths } from '../../routes/routes-path';
import { EkycCameraModal } from '../../pages/donate-goal/ekyc-camera-modal';
import { ekycErrorMessage } from '../../pages/donate-goal/ekyc-flow';
import EditProfiledDialog from '../edit-profile-dialog/edit-profile-dialog';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import {
  ELIGIBILITY_REMINDER,
  REQUIRED_GROUP_TITLE,
  REVIEW_GROUP_TITLE,
  SubmitState,
  ekycBlockedReason,
  itemStatusText,
  progressText,
  requestStateText,
  splitGroups,
  submitState,
} from './eligibility-rules';

export interface EligibilitySubmitSlot extends SubmitState {
  result: EligibilityResult;
  /** Tải lại danh sách sau khi gửi yêu cầu xong. */
  reload: () => void;
}

interface Props {
  kind: EligibilityKind;
  /**
   * Nguồn dữ liệu khác endpoint của người dùng, ví dụ admin xem hộ một tài khoản.
   * Khi truyền vào thì nên kèm `loaderKey` đổi theo tài khoản để tải lại đúng lúc.
   */
  loader?: () => Promise<EligibilityResult>;
  loaderKey?: string;
  /** Chỉ xem: không có nút làm ngay, không có khung trạng thái yêu cầu, không có câu nhắc. */
  readOnly?: boolean;
  /** Chữ mặc định của nút gửi. Bị từ chối và đã tới hạn thì nút đổi thành "Gửi lại yêu cầu". */
  submitLabel?: string;
  /** Vẽ nút gửi yêu cầu. Không gọi khi yêu cầu đang chờ duyệt hoặc đã duyệt. */
  renderSubmit?: (slot: EligibilitySubmitSlot) => React.ReactNode;
  /** Báo kết quả mỗi lần tải xong. */
  onLoaded?: (result: EligibilityResult) => void;
}

const statusTone = (met: boolean | null | undefined) =>
  met === true
    ? 'text-emerald-600 dark:text-emerald-400'
    : met === false
      ? 'text-red-600 dark:text-red-400'
      : 'text-amber-600 dark:text-amber-400';

const StatusIcon = ({ met }: { met: boolean | null | undefined }) =>
  met === true ? (
    <CheckCircle2 size={18} aria-hidden />
  ) : met === false ? (
    <XCircle size={18} aria-hidden />
  ) : (
    <CircleDashed size={18} aria-hidden />
  );

const actionBtn =
  'inline-flex items-center rounded-lg bg-[#0057FF] px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50';

const EligibilityChecklist = ({
  kind,
  loader,
  loaderKey,
  readOnly = false,
  submitLabel = 'Gửi yêu cầu',
  renderSubmit,
  onLoaded,
}: Props) => {
  const [result, setResult] = useState<EligibilityResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [tick, setTick] = useState(0);
  const [ekycOpen, setEkycOpen] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const loaderRef = useRef(loader);
  loaderRef.current = loader;
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;

  const reload = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setFailed(false);
    const load = loaderRef.current ?? (() => getEligibility(kind));
    load()
      .then(
        (r) => {
          if (!alive) return;
          setResult(r);
          setLoading(false);
          onLoadedRef.current?.(r);
        },
        () => {
          if (!alive) return;
          setFailed(true);
          setLoading(false);
        }
      );
    return () => {
      alive = false;
    };
  }, [kind, loaderKey, tick]);

  if (loading && !result) {
    return <p className="ui-muted text-sm">Cab đang tải danh sách điều kiện…</p>;
  }
  if (failed && !result) {
    return (
      <div className="flex flex-col gap-2 text-sm">
        <p className="ui-muted">Cab chưa tải được danh sách điều kiện.</p>
        <button type="button" className={`${actionBtn} w-fit`} onClick={reload}>
          Tải lại
        </button>
      </div>
    );
  }
  if (!result) return null;

  const { required, review } = splitGroups(result.items);
  const stateText = readOnly ? null : requestStateText(result.request, kind);
  const submit = submitState(result, submitLabel);

  return (
    <div className="flex flex-col gap-4" data-testid="eligibility-checklist">
      {stateText && (
        <div
          role="status"
          className={`rounded-lg px-3 py-2 text-sm ${
            result.request?.status === 'rejected'
              ? 'bg-amber-50 text-amber-800 dark:bg-amber-500/10 dark:text-amber-300'
              : result.request?.status === 'approved'
              ? 'bg-slate-100 text-slate-800 dark:bg-slate-700/50 dark:text-slate-200'
              : 'bg-blue-50 text-blue-800 dark:bg-blue-500/10 dark:text-blue-300'
          }`}
        >
          {stateText}
          {result.request?.status === 'approved' && <SupportContact />}
        </div>
      )}

      <ItemGroup
        title={REQUIRED_GROUP_TITLE}
        items={required}
        allItems={result.items}
        readOnly={readOnly}
        onEkyc={() => setEkycOpen(true)}
        sendingEmail={sendingEmail}
        setSendingEmail={setSendingEmail}
        reload={reload}
      />
      <ItemGroup
        title={REVIEW_GROUP_TITLE}
        items={review}
        allItems={result.items}
        readOnly={readOnly}
        onEkyc={() => setEkycOpen(true)}
        sendingEmail={sendingEmail}
        setSendingEmail={setSendingEmail}
        reload={reload}
      />

      {!readOnly && <p className="ui-muted text-sm leading-relaxed">{ELIGIBILITY_REMINDER}</p>}

      {!readOnly && renderSubmit && !submit.hidden && renderSubmit({ ...submit, result, reload })}

      {!readOnly && (
        <EkycCameraModal
          open={ekycOpen}
          identityOnly
          onClose={() => setEkycOpen(false)}
          onVerified={() => {
            setEkycOpen(false);
            toast.success('Bạn đã xác minh danh tính xong.');
            reload();
          }}
        />
      )}
    </div>
  );
};

/** Email và link hỗ trợ lấy từ cài đặt công khai, chỉ tải khi cần hiện (yêu cầu đã duyệt mà bị tắt lại). */
const SupportContact = () => {
  const { get } = useFeatureFlags();
  const email = get('support.email').trim();
  const link = get('support.link').trim();
  if (!email && !link) {
    return <p className="mt-1">Bạn nhắn bộ phận hỗ trợ của Cab để được xem lại.</p>;
  }
  return (
    <p className="mt-1 flex flex-wrap gap-3">
      {email && (
        <a href={`mailto:${email}`} className="text-blue-600 hover:underline">
          {email}
        </a>
      )}
      {link && (
        <a href={link} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
          Liên hệ hỗ trợ
        </a>
      )}
    </p>
  );
};

interface GroupProps {
  title: string;
  items: EligibilityItem[];
  allItems: EligibilityItem[];
  readOnly: boolean;
  onEkyc: () => void;
  sendingEmail: boolean;
  setSendingEmail: (v: boolean) => void;
  reload: () => void;
}

const ItemGroup = ({ title, items, ...rest }: GroupProps) => {
  if (items.length === 0) return null;
  return (
    <section className="flex flex-col gap-2" aria-label={title}>
      <h3 className="ui-heading text-base font-semibold">{title}</h3>
      <ul className="ui-card flex flex-col rounded-xl">
        {items.map((item) => (
          <ItemRow key={item.key} item={item} {...rest} />
        ))}
      </ul>
    </section>
  );
};

const ItemRow = ({
  item,
  allItems,
  readOnly,
  onEkyc,
  sendingEmail,
  setSendingEmail,
  reload,
}: { item: EligibilityItem } & Omit<GroupProps, 'title' | 'items'>) => {
  const progress = progressText(item);
  return (
    <li
      className="flex flex-col gap-2 border-b ui-divider px-3 py-3 last:border-b-0 sm:flex-row sm:items-start sm:justify-between"
      data-testid={`eligibility-item-${item.key}`}
    >
      <div className="flex min-w-0 gap-2">
        <span className={`mt-0.5 shrink-0 ${statusTone(item.met)}`}>
          <StatusIcon met={item.met} />
        </span>
        <div className="min-w-0">
          <p className="ui-text text-sm font-medium">{item.label}</p>
          {item.hint && <p className="ui-muted mt-0.5 text-xs">{item.hint}</p>}
        </div>
      </div>
      <div className="flex shrink-0 flex-col items-start gap-1.5 pl-7 sm:items-end sm:pl-0">
        <span className={`text-sm font-medium ${statusTone(item.met)}`}>
          {itemStatusText(item.met)}
          {progress && <span className="ui-muted font-normal">{` · ${progress}`}</span>}
        </span>
        {!readOnly && item.met === false && (
          <ItemAction
            item={item}
            allItems={allItems}
            onEkyc={onEkyc}
            sendingEmail={sendingEmail}
            setSendingEmail={setSendingEmail}
            reload={reload}
          />
        )}
      </div>
    </li>
  );
};

const ItemAction = ({
  item,
  allItems,
  onEkyc,
  sendingEmail,
  setSendingEmail,
  reload,
}: { item: EligibilityItem } & Omit<GroupProps, 'title' | 'items' | 'readOnly'>) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const profile = useSelector((state: RootState) => state.features.auth.profile);

  switch (item.key) {
    case 'email':
      return (
        <button
          type="button"
          className={actionBtn}
          disabled={sendingEmail || !profile?.email}
          onClick={async () => {
            if (!profile?.email) return;
            setSendingEmail(true);
            try {
              await authenticationServices.resendVerifyEmail(profile.email);
              toast.success('Cab đã gửi link xác minh vào email của bạn. Bạn mở email và bấm vào link nhé.');
            } catch (e) {
              toast.error(ekycErrorMessage(e, 'Cab chưa gửi được email xác minh. Bạn thử lại sau nhé.'));
            } finally {
              setSendingEmail(false);
            }
          }}
        >
          Gửi email xác minh
        </button>
      );
    case 'ekyc': {
      const blocked = ekycBlockedReason(allItems);
      return (
        <>
          <button type="button" className={actionBtn} disabled={!!blocked} onClick={onEkyc}>
            Xác minh danh tính
          </button>
          {blocked && <span className="ui-muted text-xs">{blocked}</span>}
        </>
      );
    }
    case 'profile':
      if (!profile) return null;
      return (
        <div className="w-[190px]">
          <EditProfiledDialog
            profile={profile}
            onUpdateSuccess={() => {
              dispatch(fetchProfile());
              reload();
            }}
          />
        </div>
      );
    case 'donateOpen':
      return (
        <button
          type="button"
          className={actionBtn}
          onClick={() => navigate(`${routePaths.userSetting}?tab=donate`)}
        >
          Mở nhận donate
        </button>
      );
    default:
      return null;
  }
};

export default EligibilityChecklist;
